import { FC } from "react";
import classNames from "classnames";
import Tooltip from "@mui/material/Tooltip";
import { CustomTypography } from "../../../components/CustomTypography/CustomTypography";
import { Icon } from "../../../components/Icon/Icon";
import useStyles from "./styles";

type MenuItemProps = {
  iconName: string;
  text: string;
  width: number | string;
  height: number | string;
  /** True when the sidebar shows labels; false in icon-only rail mode. */
  isCollapsed: boolean;
  /** True when this item matches the current route. */
  isActive?: boolean;
  onClick?: () => void;
  inverse?: boolean;
};

export const MenuItem: FC<MenuItemProps> = ({
  iconName,
  isCollapsed,
  isActive = false,
  text,
  width,
  height,
  onClick,
  inverse,
}) => {
  const classes = useStyles();

  const body = inverse ? (
    <span className={classes.inverseMenu}>
      {isCollapsed && (
        <span className={classes.labelSlot}>
          <CustomTypography text={text} variant={"h5"} isUppercase />
        </span>
      )}
      <span className={classes.iconSlot}>
        <Icon name={iconName} width={width} height={height} />
      </span>
    </span>
  ) : (
    <>
      <span className={classes.iconSlot}>
        <Icon name={iconName} width={width} height={height} />
      </span>
      {isCollapsed && (
        <span className={classes.labelSlot}>
          <CustomTypography text={text} variant={"h5"} isUppercase />
        </span>
      )}
    </>
  );

  const button = (
    /*
     * A real <button> rather than a <div onClick>: it is reachable with Tab,
     * fires on Enter and Space for free, and is announced as a control by
     * screen readers.
     */
    <button
      type="button"
      className={classNames(
        classes.menuItem,
        !isCollapsed && classes.rail,
        isActive && classes.active
      )}
      onClick={onClick}
      aria-current={isActive ? "page" : undefined}
      /* In rail mode the label is invisible, so name the button explicitly. */
      aria-label={!isCollapsed ? text : undefined}
    >
      {body}
    </button>
  );

  /*
   * In rail mode the icon is the only clue to what the item does, so the label
   * is surfaced on hover / focus.
   */
  if (!isCollapsed) {
    return (
      <Tooltip title={text} placement="right" arrow disableInteractive>
        {button}
      </Tooltip>
    );
  }

  return button;
};
